<template>
    <div class="container">
        <div class="page-content mt-10">
            <div class="row">
                <div class="col-12">
                    <h1 class="page-heading text-dark">
                        Poké Quiz
                    </h1>
                </div>

                <div class="col-12" v-if="!quizRunning">
                    <div v-if="questions.length > 0" class="row p-0">
                        <div class="alert alert-success result-text">
                            {{ $t('quiz.result', {correctAnswers: correctAnswers, questionsTotal: questions.length, payout: correctAnswers*10}) }} <currency-icon/>
                        </div>
                    </div>
                    <div v-else class="row p-0">
                        <div class="alert alert-light result-text">
                            <span v-html="$t('quiz.info')"></span><br/>
                            {{ $t('quiz.info2', {count: questionsPerRound}) }}
                        </div>
                    </div>

                    <a href="javascript:" class="btn btn-success w-100 start-button" v-on:click="startNewQuiz()">
                        {{ $t('quiz.start') }}
                    </a>

                    <h5 class="mt-3 fs-2 w-100 text-center">{{ $t('quiz.leaderboard.name') }}</h5>
                    <table class="table table-striped" id="leaderboard">
                        <thead>
                        <tr>
                            <th>&nbsp;</th>
                            <th>User</th>
                            <th class="text-end">{{ $t('quiz.leaderboard.correct') }}</th>
                            <th class="text-end">{{ $t('quiz.leaderboard.questions') }}</th>
                            <th class="text-end">%</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(entry, highscoreIndex) in highscores">
                            <td>#{{ highscoreIndex+1 }}</td>
                            <td><a :href="'/u/'+entry.user.username">{{ entry.user.username }}</a></td>
                            <td class="text-end">{{ entry.correct_answers }}</td>
                            <td class="text-end">{{ entry.questions_total }}</td>
                            <td class="text-end">{{ HelperService.percentage(entry.correct_answers, entry.questions_total).toFixed(0) }}%</td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="quizRunning" class="row p-0">
                    <div class="col-12 mb-1">
                        <h5>{{ $t('quiz.progress', {done: (currentQuestionIndex + 1), from: questionsPerRound}) }}</h5>
                        <div class="progress-bar progress-bar-striped bg-info progress-bar-animated rounded"
                             role="progressbar" :aria-valuenow="currentQuestionIndex" aria-valuemin="0"
                             aria-valuemax="100" :style="'width: '+(currentQuestionIndex * 10)+'%; height: 3px;'"></div>
                    </div>

                    <div class="col-12 mb-3">
                        <div class="card bg-light text-dark">
                            <div class="card-body text-center">
                                <img :src="currentQuestion.pokedexEntry.animated_artwork ?? currentQuestion.pokedexEntry.front_sprite_url"
                                     alt="" v-if="['height', 'weight', 'types', 'generation', 'name'].includes(currentQuestion.questionType)"
                                     :class="'d-block mb-2 pokemon-preview-image '+(currentQuestion.questionType === 'name' && !selectedAnswer ? 'whos-that-pokemon' : '')"
                                     style="margin-left: auto; margin-right: auto;">

                                <strong class="question-text" v-if="currentQuestion.questionType === 'flavor_text'">{{ getCensoredFlavorText() }}</strong>
                                <strong class="question-text" v-else>{{ getQuestionText() }}</strong>

                                <a class="btn btn-sm btn-info d-block mt-1" v-if="currentQuestion.questionType === 'cry'" v-on:click="playCry(currentQuestion.pokedexEntry.cry_url)">
                                    <i class="fa-solid fa-play"></i> {{ $t('quiz.questions.playCry') }}
                                </a>
                            </div>
                        </div>
                    </div>

                    <div class="col-6 mb-2" v-for="answer in currentQuestion.answers" v-if="!selectedAnswer">
                        <a class="btn btn-warning w-100 px-0 answer-text" v-on:click="selectAnswer(answer)">
                            <span v-if="currentQuestion.questionType === 'weight'">
                                {{ answer.text / 1000 }}kg
                            </span>

                            <span v-if="currentQuestion.questionType === 'height'">
                                {{ answer.text / 100 }}m
                            </span>

                            <span v-if="currentQuestion.questionType === 'generation'">
                                Generation {{ answer.text }}
                            </span>

                            <span v-if="currentQuestion.questionType === 'types'">
                                <type-icon v-for="type in answer.text.split(',')" :type="type"/>
                            </span>

                            <span v-if="!['weight', 'height', 'types', 'generation'].includes(currentQuestion.questionType)">
                                {{ answer.text }}
                            </span>

                            <div v-if="['cry', 'flavor_text', 'is_legendary', 'is_baby'].includes(currentQuestion.questionType)" style="height: 8em; display: flex; align-items: flex-end;">
                                <img :src="answer.pokedexEntry.animated_artwork ?? answer.pokedexEntry.front_sprite_url"
                                     alt="" class="d-block mt-4" style="max-height: 80%; margin: auto;">
                            </div>
                        </a>
                    </div>

                    <div class="col-12" v-else>
                        <div :class="'result-text alert alert-'+(selectedAnswer.is_correct ? 'success' : 'warning')">
                            <b class="fw-bold" v-if="selectedAnswer.is_correct">{{ $t('quiz.solution.correct') }}</b>
                            <b class="fw-bold" v-else>{{ $t('quiz.solution.wrong') }}</b>

                            <p v-if="currentQuestion.questionType === 'weight'">
                                {{ $t('quiz.solution.weight', {name: getTranslatedName(currentQuestion.pokedexEntry), answer: currentQuestion.correctAnswer / 1000}) }}
                            </p>

                            <p v-if="currentQuestion.questionType === 'height'">
                                {{ $t('quiz.solution.height', {name: getTranslatedName(currentQuestion.pokedexEntry), answer: currentQuestion.correctAnswer / 100}) }}
                            </p>

                            <p v-if="currentQuestion.questionType === 'types'">
                                {{ $t('quiz.solution.types', {name: getTranslatedName(currentQuestion.pokedexEntry) }) }} <type-icon v-for="type in currentQuestion.correctAnswer.split(',')" :type="type"/>
                            </p>

                            <p v-if="currentQuestion.questionType === 'generation'">
                                {{ $t('quiz.solution.generation', {name: getTranslatedName(currentQuestion.pokedexEntry), generation: currentQuestion.correctAnswer }) }}
                            </p>

                            <p v-if="!['weight', 'height', 'types', 'generation'].includes(currentQuestion.questionType)">
                                {{ $t('quiz.answers.correct', {answer: getTranslatedName(currentQuestion.pokedexEntry)}) }}

                                <img v-if="['cry', 'flavor_text', 'is_legendary', 'is_baby'].includes(currentQuestion.questionType)"
                                     :src="currentQuestion.pokedexEntry.animated_artwork ?? currentQuestion.pokedexEntry.front_sprite_url"
                                     alt="" class="d-block mt-4 pokemon-preview-image" style="margin-left: auto; margin-right: auto;">
                            </p>
                        </div>

                        <a href="javascript:" class="btn btn-info w-100" v-on:click="continueToNextQuestion">
                            {{ (this.currentQuestionIndex+1) === this.questions.length ? $t('quiz.finish') : $t('quiz.nextQuestion') }}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CurrencyIcon from "../parts/CurrencyIcon.vue";
import TypeIcon from "../parts/TypeIcon.vue";
import HelperService from "../../HelperService";

export default {
    name: "Quiz",
    components: {TypeIcon, CurrencyIcon},
    data() {
        return {
            quizRunning: false,
            questions: [],
            currentQuestion: null,
            currentQuestionIndex: 0,
            selectedAnswer: null,
            answeredQuestions: [],
            questionsPerRound: 10,
            correctAnswers: 0,
            highscores: [],
        };
    },
    mounted() {
        this.$store.commit('updateUserData');
        this.fetchLeaderboard();
    },
    computed: {
        HelperService() {
            return HelperService
        }
        //
    },
    watch: {
        //
    },
    methods: {
        fetchLeaderboard() {
            axios.get('/api/quiz/highscores').then(response => {
                this.highscores = response.data;
            })
        },
        startNewQuiz() {
            this.fetchQuestions();
        },
        getQuestionText() {
            let type = this.currentQuestion.questionType;

            switch (type) {
                case 'name':
                    return this.$i18n.t('quiz.questions.name');
                case 'is_legendary':
                    return this.$i18n.t('quiz.questions.legendary');
                case 'is_baby':
                    return this.$i18n.t('quiz.questions.baby');
                case 'weight':
                    return this.$i18n.t('quiz.questions.weight', {name: this.getTranslatedName(this.currentQuestion.pokedexEntry)});
                case 'types':
                    return this.$i18n.t('quiz.questions.types', {name: this.getTranslatedName(this.currentQuestion.pokedexEntry)});
                case 'height':
                    return this.$i18n.t('quiz.questions.height', {name: this.getTranslatedName(this.currentQuestion.pokedexEntry)});
                case 'generation':
                    return this.$i18n.t('quiz.questions.generation', {name: this.getTranslatedName(this.currentQuestion.pokedexEntry)});
                case 'flavortext':
                    return this.currentQuestion.question;
                case 'cry':
                    return this.$i18n.t('quiz.questions.cry');
            }
        },
        fetchQuestions() {
            this.questions = [];

            fetch('/api/quiz/questions/'+this.questionsPerRound).then(response => response.json()).then((response) => {
                this.questions = response;
                this.correctAnswers = 0;
                this.currentQuestionIndex = 0;
                this.currentQuestion = this.questions[this.currentQuestionIndex];
                this.quizRunning = true;
            });
        },
        selectAnswer(answer) {
            this.selectedAnswer = answer;
        },
        playCry(url) {
            let player = new Audio(url)
            player.volume = 0.5;
            player.play();
        },
        getCensoredFlavorText() {
            return this.currentQuestion.question
                .replaceAll(this.currentQuestion.correctAnswer, '******')
                .replaceAll(this.currentQuestion.correctAnswer.toUpperCase(), '******')
        },
        continueToNextQuestion() {
            if (this.selectedAnswer.is_correct) {
                this.correctAnswers++;
                this.currentQuestion.pickedAnswer = this.selectedAnswer.text;
                this.selectedAnswer = null;
            } else {
                this.currentQuestion.pickedAnswer = this.selectedAnswer.text;
                this.selectedAnswer = null;
            }

            if ((this.currentQuestionIndex+1) === this.questions.length) {
                this.quizRunning = false;
                this.submitPlayedGame();
            } else {
                this.currentQuestion = this.questions[++this.currentQuestionIndex];
            }
        },
        submitPlayedGame() {
            axios.post('/api/quiz/submit', {
                questions: this.questions,
                correctAnswers: this.correctAnswers
            }).then((response) => {
                if (response.data.payout > 0) {
                    let title = this.$i18n.t('quiz.winNotificaton', {payout: response.data.payout}) + '<img style="max-height: 1.5em;" src="/assets/dipocaco/currency_x64.png" alt="currency-icon">';

                    iziToast.success({
                        title: title,
                        iconUrl: ''
                    });

                    this.fetchLeaderboard()
                }
            })
        },
        getTranslatedName(pokedexEntry) {
            return this.$i18n.locale === 'de' ? pokedexEntry.name_de : pokedexEntry.name;
        }
    },
};
</script>


<style scoped lang="scss">
.page-heading {
    background-image: url(/assets/dipocaco/pocas/quiz-poca_sm.jpg);
    background-position: center 45%;
}

.start-button {
    font-size: 1.5em;
    padding: 1em 0;
}

.whos-that-pokemon {
    filter: blur(3px);
}

.pokemon-preview-image {
    max-height: 7.5em;
}

.question-text {
    font-size: 1.5em;
}

.answer-text {
    font-size: 1.4em;
}

.result-text {
    font-size: 1.35em;
}

.currency-icon {
    height: 1.2em;
}

#leaderboard {
    font-size: 1.25em;

    a:any-link {
        color: initial !important;
        text-decoration: none !important;
    }
}
</style>
